import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { UserProfile, LoginCredentials, SignupData } from '../types';
import * as authService from '../services/authService';
import { uploadFile } from '../services/fileUploadService';
import { getUserProfile, patchIdentityFields, patchProfileFields, updateProfileAddress } from '../services/profileService';
import { changePassword } from '../services/settingsService';
import { getUserVerificationStatus } from '../services/verificationService';
import { buildAddressPayload, buildProfileChangeSet, hasAddressValues, validateAddressPayload } from '../utils/profileFieldMapping';
import { API_KEY, CLIENT_APP } from '../services/clientConfig';

interface AuthContextType {
  user: UserProfile | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  getAccessToken: () => string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  logout: () => Promise<void>;
  googleLogin: (credential: string) => Promise<any>;
  appleLogin: (response: any) => Promise<any>;
  microsoftLogin: (idToken: string) => Promise<any>;
  initiateLinkedInWebSignIn: (returnTo: string) => Promise<{ redirectUrl: string }>;
  linkedinLogin: (tokenData: { accessToken: string; refreshToken?: string; accessTokenExpiresAt?: number; refreshTokenExpiresAt?: number }) => Promise<any>;
  startPasswordRecovery: (identifier: string) => Promise<any>;
  verifyRecoveryCode: (otpToken: string, code: string) => Promise<any>;
  resetPassword: (resetToken: string, newPassword: string, confirmPassword: string) => Promise<void>;
  refreshAuthState: () => void;
  refreshProfile: () => Promise<UserProfile | null>;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  updatePassword: (currentPassword: string, newPassword: string, confirmPassword: string) => Promise<void>;
  uploadProfilePicture: (file: File) => Promise<string | null>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const fallbackAuthContext: AuthContextType = {
  user: null,
  isLoggedIn: false,
  isLoading: true,
  getAccessToken: () => null,
  login: async () => {},
  signup: async () => {},
  logout: async () => {},
  googleLogin: async () => null,
  appleLogin: async () => null,
  microsoftLogin: async () => null,
  initiateLinkedInWebSignIn: async () => ({ redirectUrl: '' }),
  linkedinLogin: async () => null,
  startPasswordRecovery: async () => null,
  verifyRecoveryCode: async () => null,
  resetPassword: async () => {},
  refreshAuthState: () => {},
  refreshProfile: async () => null,
  updateProfile: async () => {},
  updatePassword: async () => {},
  uploadProfilePicture: async () => null,
  deleteAccount: async () => {},
};

export const useAuth = () => {
  return useContext(AuthContext) || fallbackAuthContext;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshAuthState = useCallback(() => {
    const storedUser = authService.getUser();
    const loggedIn = authService.isLoggedIn();
    console.log('🔄 Refreshing auth state:', { loggedIn, user: storedUser });
    
    setUser(loggedIn ? storedUser : null);
    setIsLoading(false);
  }, []);

  const refreshProfile = useCallback(async (): Promise<UserProfile | null> => {
    const token = authService.getAccessToken();
    if (!token) {
      refreshAuthState();
      return null;
    }

    const currentUser = authService.getUser() || {};
    try {
      const [profile, verificationStatus] = await Promise.all([
        getUserProfile(true),
        getUserVerificationStatus(token).catch(() => null),
      ]);
      const normalized = authService.normalizeUserProfile(
        {
          ...profile,
          ...(verificationStatus || {}),
        },
        currentUser
      );
      authService.saveUser(normalized);
      setUser(normalized);
      window.dispatchEvent(new Event('auth-state-change'));
      return normalized;
    } catch (error) {
      console.warn('Failed to refresh profile:', error);
      refreshAuthState();
      return authService.getUser();
    }
  }, [refreshAuthState]);

  // Initial load
  useEffect(() => {
    if (authService.getAccessToken()) {
      refreshProfile();
    } else {
      refreshAuthState();
    }
  }, [refreshAuthState, refreshProfile]);

  // Listen for auth storage changes and explicit auth refresh events.
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key === 'olitask_access_token' ||
        event.key === 'olitask_user' ||
        event.key === 'olitask_refresh_token'
      ) {
        console.log('📦 Storage changed, refreshing auth state');
        refreshAuthState();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    const handleCustomAuthChange = () => {
      refreshAuthState();
    };
    window.addEventListener('auth-state-change', handleCustomAuthChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-state-change', handleCustomAuthChange);
    };
  }, [refreshAuthState]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    await authService.login(credentials);
    await refreshProfile();
  }, [refreshProfile]);

  const signup = useCallback(async (data: SignupData) => {
    const userData = await authService.signupAndLogin(data);
    await refreshProfile();
    return userData;
  }, [refreshProfile]);

  const logout = useCallback(async () => {
    await authService.logout();
    window.dispatchEvent(new Event('auth-state-change'));
    refreshAuthState();
  }, [refreshAuthState]);

  const googleLogin = useCallback(async (credential: string) => {
    const result = await authService.googleLogin(credential);
    if (result?.accessToken || result?.token) {
      await refreshProfile();
    }
    return result;
  }, [refreshProfile]);

  const appleLogin = useCallback(async (response: any) => {
    const result = await authService.appleLogin(response);
    if (result?.accessToken || result?.token) {
      await refreshProfile();
    }
    return result;
  }, [refreshProfile]);

  const microsoftLogin = useCallback(async (idToken: string) => {
    const result = await authService.microsoftLogin(idToken);
    if (result?.accessToken || result?.token) {
      await refreshProfile();
    }
    return result;
  }, [refreshProfile]);

  const initiateLinkedInWebSignIn = useCallback(
    (returnTo: string) => authService.initiateLinkedInWebSignIn(returnTo),
    [],
  );

  const linkedinLogin = useCallback(async (tokenData: any) => {
    const result = await authService.linkedinLogin(tokenData);
    if (result?.data?.accessToken) {
      await refreshProfile();
    }
    return result;
  }, [refreshProfile]);

  const startPasswordRecovery = useCallback((identifier: string) => authService.startPasswordRecovery(identifier), []);
  const verifyRecoveryCode = useCallback((otpToken: string, code: string) => authService.verifyPasswordRecoveryCode(otpToken, code), []);
  const resetPassword = useCallback((resetToken: string, newPassword: string, confirmPassword: string) => 
    authService.resetPassword(resetToken, newPassword, confirmPassword), []);

  const updateProfile = useCallback(async (data: Partial<UserProfile>) => {
    const token = authService.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated. Please sign in first.');
    }

    const userId = user?.id || data.id;
    if (!userId) {
      throw new Error('Your profile is still loading. Please refresh and try again.');
    }

    const currentProfile = user || authService.getUser() || {};
    const { profileChanges, identityChanges } = buildProfileChangeSet(data, currentProfile);

    try {
      let updatedUser: any = authService.getUser() || {};

      if (identityChanges.length) {
        await patchIdentityFields(identityChanges);
      }

      if (profileChanges.length) {
        updatedUser = await patchProfileFields(profileChanges);
      }

      const addressPayload = buildAddressPayload(data);
      if (hasAddressValues(addressPayload)) {
        validateAddressPayload(addressPayload);
        await updateProfileAddress(userId, addressPayload);
      }

      let mergedUser = authService.normalizeUserProfile(
        { ...updatedUser, ...data, id: userId },
        { ...(authService.getUser() || {}), ...data, id: userId }
      );

      try {
        const freshProfile = await getUserProfile(true);
        mergedUser = authService.normalizeUserProfile(freshProfile, mergedUser);
      } catch (refreshError) {
        console.warn('Profile updated but refresh failed:', refreshError);
      }

      authService.saveUser(mergedUser);
      setUser(mergedUser);
      window.dispatchEvent(new Event('auth-state-change'));
      refreshAuthState();
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }, [user, refreshAuthState]);

  const updatePassword = useCallback(async (currentPassword: string, newPassword: string, confirmPassword: string) => {
    if (newPassword !== confirmPassword) {
      throw new Error('New passwords do not match');
    }
    if (newPassword.length < 8) {
      throw new Error('Password must be at least 8 characters');
    }
    
    try {
      await changePassword(currentPassword, newPassword);
    } catch (error) {
      console.error('Update password error:', error);
      throw error;
    }
  }, []);

  const uploadProfilePicture = useCallback(async (file: File): Promise<string | null> => {
    const token = authService.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated. Please sign in first.');
    }
    if (!user?.id) {
      throw new Error('Your profile is still loading. Please refresh and try again.');
    }

    try {
      const result = await uploadFile(user.id, file, { purpose: 'USER_PROFILE', fileType: 'PHOTO' });
      await patchProfileFields([{ name: 'profilePhoto', value: result.fileId }]);

      const publicUrl =
        result.file?.publicUrl || result.file?.url || result.file?.fileUrl || result.fileId;
      const updatedUser = { ...user, picture: publicUrl, profilePhoto: result.fileId } as UserProfile;
      authService.saveUser(updatedUser);
      setUser(updatedUser);
      window.dispatchEvent(new Event('auth-state-change'));
      refreshAuthState();
      
      return publicUrl;
    } catch (error) {
      console.error('Upload profile picture error:', error);
      throw error;
    }
  }, [user, refreshAuthState]);

  const deleteAccount = useCallback(async () => {
    const token = authService.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated. Please sign in first.');
    }

    try {
      const response = await fetch(`https://dev.oliveeb.com/profile-service/api/v1/user-profiles/me`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'O-CLIENT-APP': CLIENT_APP,
          'O-API-KEY': API_KEY,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete account');
      }
      
      await authService.logout();
      window.dispatchEvent(new Event('auth-state-change'));
      refreshAuthState();
    } catch (error) {
      console.error('Delete account error:', error);
      throw error;
    }
  }, [refreshAuthState]);

  const value = {
    user,
    isLoggedIn: !!user,
    isLoading,
    getAccessToken: authService.getAccessToken,
    login,
    signup,
    logout,
    googleLogin,
    appleLogin,
    microsoftLogin,
    initiateLinkedInWebSignIn,
    linkedinLogin,
    startPasswordRecovery,
    verifyRecoveryCode,
    resetPassword,
    refreshAuthState,
    refreshProfile,
    updateProfile,
    updatePassword,
    uploadProfilePicture,
    deleteAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
